/** Read-only billing ledger, readiness, and route-cost summary. */

import { CircleAlert, CircleCheck, Clock3, ReceiptText } from 'lucide-react'
import { cn } from '../../lib/utils'
import type {
  CreditBalance,
  CreditPurchase,
  CreditTransaction,
  PaginatedCredits,
  PaymentsConfig,
} from '../../hooks/use-credits'
import { Metric, ReadinessRow } from './billing-ledger-parts'
import { LedgerTable } from './billing-ledger-table'
import { formatMinorCurrencyAmount } from './currency-format'

interface BillingLedgerPanelProps {
  balance: CreditBalance | undefined
  paymentsConfig: PaymentsConfig | undefined
  transactions: PaginatedCredits<CreditTransaction> | undefined
  purchases: PaginatedCredits<CreditPurchase> | undefined
  transactionType: string
  onTransactionTypeChange: (type: string) => void
  onTransactionPageChange: (offset: number) => void
  onPurchasePageChange: (offset: number) => void
  transactionsLoading: boolean
  purchasesLoading: boolean
  isDark: boolean
}

const TRANSACTION_TYPE_OPTIONS = [
  { value: 'all', label: 'All types' },
  { value: 'purchase', label: 'Purchases' },
  { value: 'deduct', label: 'Usage' },
  { value: 'free_grant', label: 'Grants' },
  { value: 'refund', label: 'Refunds' },
  { value: 'expire', label: 'Expired' },
]

function formatDate(value: number | null | undefined) {
  if (!value) return 'Not recorded'
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function signedAmount(value: number) {
  const prefix = value > 0 ? '+' : ''
  return `${prefix}${value.toLocaleString()}`
}

export function BillingLedgerPanel({
  balance,
  paymentsConfig,
  transactions,
  purchases,
  transactionType,
  onTransactionTypeChange,
  onTransactionPageChange,
  onPurchasePageChange,
  transactionsLoading,
  purchasesLoading,
  isDark,
}: BillingLedgerPanelProps) {
  const panelClass = cn(
    'rounded-xl border p-5',
    isDark ? 'border-white/[0.06] bg-surface-1' : 'border-neutral-200 bg-white',
  )
  const mutedText = isDark ? 'text-neutral-500' : 'text-neutral-500'
  const strongText = isDark ? 'text-neutral-100' : 'text-neutral-900'
  const subtlePanel = isDark
    ? 'border-white/[0.06] bg-surface-2'
    : 'border-neutral-200 bg-neutral-50'
  const routeCosts = paymentsConfig?.routeCosts ?? []
  const ready = Boolean(paymentsConfig?.providerConfigured && paymentsConfig?.webhookConfigured)

  return (
    <section className={panelClass} aria-label="Billing ledger">
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <ReceiptText className={cn('h-4 w-4', mutedText)} />
            <h2 className={cn('text-sm font-semibold', strongText)}>Billing ledger</h2>
          </div>
          <p className={cn('mt-1 text-xs', mutedText)}>
            Balance changes, purchases, route costs, and payment readiness.
          </p>
        </div>
        <div
          className={cn('flex items-center gap-2 rounded-lg border px-3 py-2 text-xs', subtlePanel)}
        >
          {ready ? (
            <CircleCheck className="h-4 w-4 text-emerald-500" />
          ) : (
            <CircleAlert className="h-4 w-4 text-amber-500" />
          )}
          <span className={cn('font-medium', strongText)}>
            {paymentsConfig?.environment === 'production' ? 'Production' : 'Sandbox'}
          </span>
          <span className={mutedText}>{ready ? 'ready' : 'not ready'}</span>
        </div>
      </div>

      <div className="mt-5 grid gap-3 md:grid-cols-3">
        <Metric
          label="Purchased"
          value={(balance?.purchasedCredits ?? 0).toLocaleString()}
          isDark={isDark}
        />
        <Metric label="Free" value={(balance?.freeCredits ?? 0).toLocaleString()} isDark={isDark} />
        <Metric
          label="Last free reset"
          value={formatDate(balance?.freeCreditsLastResetAt)}
          isDark={isDark}
        />
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        <LedgerTable
          title="Recent transactions"
          empty="No transactions yet."
          isLoading={transactionsLoading}
          isDark={isDark}
          total={transactions?.total ?? 0}
          limit={transactions?.limit ?? 5}
          offset={transactions?.offset ?? 0}
          hasMore={transactions?.hasMore ?? false}
          filterValue={transactionType}
          filterOptions={TRANSACTION_TYPE_OPTIONS}
          onFilterChange={onTransactionTypeChange}
          onPageChange={onTransactionPageChange}
          rows={(transactions?.data ?? []).map((item) => ({
            id: item.id,
            primary: item.description ?? item.type,
            secondary: `${item.type} · balance ${item.balanceAfter.toLocaleString()}`,
            meta: signedAmount(item.amount),
          }))}
        />
        <LedgerTable
          title="Purchase history"
          empty="No purchases yet."
          isLoading={purchasesLoading}
          isDark={isDark}
          total={purchases?.total ?? 0}
          limit={purchases?.limit ?? 5}
          offset={purchases?.offset ?? 0}
          hasMore={purchases?.hasMore ?? false}
          onPageChange={onPurchasePageChange}
          rows={(purchases?.data ?? []).map((item) => ({
            id: item.id,
            primary: item.packName,
            secondary: `${item.status} · ${formatMinorCurrencyAmount(item.amountPaid, item.currency)}`,
            meta: `+${item.creditsGranted.toLocaleString()}`,
          }))}
        />
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_1.4fr]">
        <div className={cn('rounded-lg border p-4', subtlePanel)}>
          <div className="flex items-center gap-2">
            <Clock3 className={cn('h-4 w-4', mutedText)} />
            <h3 className={cn('text-xs font-semibold uppercase tracking-wider', mutedText)}>
              Payment readiness
            </h3>
          </div>
          <div className="mt-3 space-y-2 text-sm">
            <ReadinessRow
              label="Provider"
              ok={Boolean(paymentsConfig?.providerConfigured)}
              isDark={isDark}
            />
            <ReadinessRow
              label="Webhook"
              ok={Boolean(paymentsConfig?.webhookConfigured)}
              isDark={isDark}
            />
            <div className="flex justify-between gap-3">
              <span className={mutedText}>Pack products</span>
              <span className={cn('font-medium', strongText)}>
                {paymentsConfig
                  ? `${paymentsConfig.packProductCoverage.configured}/${paymentsConfig.packProductCoverage.active} configured`
                  : 'Unknown'}
              </span>
            </div>
          </div>
        </div>

        <div className={cn('rounded-lg border p-4', subtlePanel)}>
          <h3 className={cn('text-xs font-semibold uppercase tracking-wider', mutedText)}>
            Route costs
          </h3>
          {routeCosts.length === 0 ? (
            <p className={cn('mt-3 text-sm', mutedText)}>No route-cost metadata available.</p>
          ) : (
            <div className="mt-3 divide-y divide-neutral-200 text-sm dark:divide-white/[0.06]">
              {routeCosts.slice(0, 6).map((cost) => (
                <div
                  key={cost.key}
                  className="grid grid-cols-[1fr_auto] gap-3 py-2 first:pt-0 last:pb-0"
                >
                  <div>
                    <div className={cn('font-medium', strongText)}>{cost.label}</div>
                    <div className={cn('text-xs', mutedText)}>
                      {cost.method} {cost.pathPrefix}
                    </div>
                  </div>
                  <div className={cn('font-semibold', strongText)}>
                    {cost.cost.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
